import {
  AuditBadge,
  Button,
  ConfirmSheet,
  DangerActionPanel,
  MetricCard,
  PageHeader,
  StatusChip,
} from "@jod/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { hasPermission } from "../../features/ops/permissions.ts";
import { useSessionTransition, useSocialPause, useStaffState } from "../../features/ops/queries.ts";
import { getStaffPrincipal } from "../../lib/auth-session.ts";

export const Route = createFileRoute("/_app/today")({ component: TodayScreen });

function TodayScreen() {
  const principal = getStaffPrincipal();
  const navigate = useNavigate();
  const { data } = useStaffState();
  const transition = useSessionTransition();
  const socialPause = useSocialPause();
  const [confirm, setConfirm] = useState(false);
  if (!data) return <PageHeader title="Today" subtitle="Loading shift state…" />;

  const pendingOrders = data.orders.filter((order) =>
    ["pending", "accepted", "preparing", "ready"].includes(order.status),
  ).length;
  const acceptedGifts = data.gifts.filter((gift) => gift.status === "accepted").length;
  const reports = data.reports.filter((report) =>
    ["submitted", "triaged", "escalated"].includes(report.status),
  ).length;
  const activeTables = data.tables.filter((table) => table.status === "occupied").length;
  const attention = [
    ...data.orders
      .filter((order) => order.status === "pending")
      .slice(0, 2)
      .map((order) => ({
        id: order.id,
        label: `${order.tableLabel}: order awaiting accept`,
        tone: "warning" as const,
      })),
    ...data.gifts
      .filter((gift) => gift.status === "accepted")
      .slice(0, 2)
      .map((gift) => ({
        id: gift.id,
        label: `${gift.recipientTableLabel}: accepted gift awaiting fulfilment`,
        tone: "info" as const,
      })),
    ...data.reports
      .filter((report) => ["high", "critical"].includes(report.severity))
      .slice(0, 2)
      .map((report) => ({
        id: report.id,
        label: `${report.severity} report: ${report.reason}`,
        tone: "danger" as const,
      })),
  ];

  const nextLabel = {
    scheduled: "Open session",
    open: "Move to last call",
    last_call: "Start closing",
    closing: "Close session",
    closed: "Closed",
  }[data.session.status];
  const canManage = hasPermission(principal, "session:manage");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Today / คืนนี้"
        subtitle={`${data.branch.name} · ${data.session.label}`}
        actions={
          canManage && data.session.status !== "closed" ? (
            <Button onClick={() => setConfirm(true)}>{nextLabel}</Button>
          ) : undefined
        }
      />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <MetricCard
          label="Session state"
          value={data.session.status}
          caption="scheduled → open → last_call → closing → closed"
          tone={data.session.status === "open" ? "success" : "neutral"}
        />
        <MetricCard
          label="Active tables"
          value={activeTables}
          caption="social indicators remain staff-only"
          tone="success"
        />
        <MetricCard
          label="Pending orders"
          value={pendingOrders}
          caption="queue to serve"
          tone="warning"
        />
        <MetricCard
          label="Accepted gifts"
          value={acceptedGifts}
          caption="awaiting fulfilment"
          tone="info"
        />
        <MetricCard
          label="Reports"
          value={reports}
          caption="severity + recency sorted"
          tone={reports > 0 ? "danger" : "neutral"}
        />
      </div>
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display font-semibold text-lg">Attention list</h2>
          <AuditBadge label="PII-minimized" />
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {attention.map((item) => (
            <StatusChip key={item.id} tone={item.tone}>
              {item.label}
            </StatusChip>
          ))}
        </div>
      </section>
      {hasPermission(principal, "social:disable") ? (
        <DangerActionPanel
          title={
            data.session.socialPaused
              ? "Emergency social mode is paused"
              : "Emergency social disable"
          }
          description={
            data.session.socialPaused
              ? "Pauses Around, gifting, contact requests, and games for this VenueSession only. Ordering remains available; in-flight pending social interactions are voided in mock state."
              : "Requires a reason (FR-STAFF-093, SAFE-012) — captured on the Session control screen."
          }
          actionLabel={data.session.socialPaused ? "Re-enable social" : "Go to Session control"}
          onAction={() =>
            data.session.socialPaused
              ? socialPause.mutate({ paused: false })
              : navigate({ to: "/session-control" })
          }
        />
      ) : null}
      <ConfirmSheet
        open={confirm}
        onOpenChange={setConfirm}
        title={`${nextLabel}?`}
        description="This consequential transition is audit logged in mock state and must be enforced server-side in production."
        confirmLabel={nextLabel}
        onConfirm={() => transition.mutate()}
      />
    </div>
  );
}
